import { IDBPDatabase } from 'idb'
import * as R from 'ramda'
import {
  DBFind,
  DBFindMany,
  DBInsert,
  dbPromise,
  DBRemove,
  DBUpdateMany,
  DBUpdateOne,
} from '@weblite/persist'
import { Message, MessageRange } from '@weblite/ts-action/message'
import { createKeyvalStorage } from '$logic/lib/keyval.storage'
import { dateWithOffset } from '$setup/time-offset'

const messageUpdateStorage = createKeyvalStorage<{
  latestUpdateTime: number
}>('message-update')

export const messageLatestUpdateTimeStorage = {
  get() {
    return messageUpdateStorage.get()?.latestUpdateTime ?? dateWithOffset()
  },
  updateTime() {
    return messageUpdateStorage.set({ latestUpdateTime: dateWithOffset() })
  },
}

const COLLECTION = 'messages'

const deleteMessageOperator = {
  $unset: {
    type: 1,
    body: 1,
    forwardedFrom: 1,
    sender: 1,
    _sender: 1,
    wappLabel: 1,
    repliedTo: 1,
    attachment: 1,
  },
  $set: { patches: { deleted: true } },
}

/**
 * Returns sorted by `iid` ascending
 */
async function getLocalMessages(
  roomId: string,
  range: MessageRange,
  roomIid: number,
  // TODO ClientMessage[]
): Promise<{
  messages: Message[]
  complete: boolean
  undeletedCount?: number
}> {
  if ('range' in range) {
    const [start, end] = range.range
    const messages = await messagesStorage.findMany(
      IDBKeyRange.bound([roomId, start], [roomId, end]),
    )
    return { messages, complete: messages.length === end - start + 1 }
  }

  const { iid, count, position } = range

  const db: IDBPDatabase = await dbPromise
  const source = db.transaction('messages').store
  let complete = false
  const messages = []
  let undeletedCount = 0
  try {
    let cursor = await source.openCursor(
      IDBKeyRange[position === 'NEXT' ? 'lowerBound' : 'upperBound'](
        [roomId, iid],
        true,
      ),
      position === 'NEXT' ? 'next' : 'prev',
    )
    const step = position === 'NEXT' ? 1 : -1
    let currentIid = iid + step
    while (cursor) {
      const { value } = cursor
      if (value.iid !== currentIid || value.roomId !== roomId) break
      currentIid += step

      messages.push(value)
      // eslint-disable-next-line no-plusplus
      if (!value.patches?.deleted) undeletedCount++
      if (undeletedCount === count) {
        complete = true
        break
      }
      // eslint-disable-next-line no-await-in-loop
      cursor = await cursor.continue()
    }
    if (position !== 'NEXT') messages.reverse()
  } catch (e) {
    console.error(`Can't get messages from idb. `, e)
  }
  if (!complete) {
    if (position === 'PREVIOUS' && messages[0]?.iid === 1) complete = true
    if (position === 'NEXT' && R.last(messages)?.iid === roomIid)
      complete = true
  }
  return { messages, complete, undeletedCount }
}

export const messagesStorage = {
  async removeByRange(keyRanges: any) {
    await DBRemove(COLLECTION, keyRanges)
    messageLatestUpdateTimeStorage.updateTime()
  },

  async updateMany(keyRanges: any, updateData: any) {
    await DBUpdateMany(COLLECTION, keyRanges, updateData)
    messageLatestUpdateTimeStorage.updateTime()
  },

  async updateOne(keyRanges: any, updateData: any) {
    await DBUpdateOne(COLLECTION, keyRanges, updateData)
    messageLatestUpdateTimeStorage.updateTime()
  },

  findMany(keyRanges: any, options?: any) {
    return DBFind(COLLECTION, keyRanges, options)
  },

  findByIids(roomId: string, iids: number[]): Promise<Message[]> {
    return DBFindMany(
      COLLECTION,
      iids.map(iid => [roomId, iid]),
    )
  },

  insert(messages: Message[]) {
    DBInsert(COLLECTION, messages)
  },

  async deleteMany(roomId: string, iids: number[]) {
    await DBUpdateMany(
      COLLECTION,
      iids.map(iid => [roomId, iid]),
      deleteMessageOperator,
    )
    messageLatestUpdateTimeStorage.updateTime()
  },

  getLocalMessages,
}
